import { memo, ReactNode, Ref } from 'react';
import { ColumnDef, RowDef } from './types';
import { twMerge } from 'tailwind-merge';
import { LoadingRow, TablePagination, TableRow } from './components';
import { usePagination } from '@/hooks';

export type TableProps<T> = {
  rows: RowDef<T>[];
  columns: ColumnDef<T>[];
  emptyStateMessage: ReactNode;
  isLoading?: boolean;
  isFetching?: boolean;
  loadingRows?: number;
  currentPage?: number;
  totalItems?: number;
  totalPages?: number;
  withPagination?: boolean;
  onPageChange?: (page: number) => void;
  lastRowRef?: Ref<HTMLTableRowElement>;
};

const TableComponent = <T,>({
  columns,
  rows,
  emptyStateMessage,
  isLoading,
  isFetching,
  loadingRows,
  currentPage = 1,
  totalItems = 0,
  totalPages = 0,
  withPagination,
  onPageChange,
  lastRowRef,
}: TableProps<T>) => {
  const {
    pageIndex,
    nextDisabled,
    prevDisabled,
    handleNextPage,
    handlePrevPage,
    handlePageChange,
  } = usePagination(currentPage, totalPages, onPageChange);

  return (
    <>
      <div className="w-full overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  align={column.alignment ?? 'center'}
                  className={twMerge(
                    'border-b-2.5 h-8 border-transparent bg-inherit px-2.5 text-xs font-medium text-gray-400',
                    column.tdClassNames,
                  )}
                >
                  {column.render?.() ?? column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!isLoading && !rows.length && !!emptyStateMessage && (
              <tr className="h-11">
                <td colSpan={columns.length} align="center" className="text-md">
                  {emptyStateMessage}
                </td>
              </tr>
            )}
            {isLoading
              ? Array.from({ length: loadingRows ?? 0 }).map((_, rowIndex) => (
                  <LoadingRow
                    key={`loading-${rowIndex}`}
                    rowIndex={rowIndex}
                    columnsCount={columns.length}
                  />
                ))
              : rows.map((row, index) => (
                  <TableRow
                    key={row.id}
                    row={row}
                    columns={columns}
                    lastRowRef={index == rows.length - 1 ? lastRowRef : null}
                  />
                ))}
            {isFetching &&
              Array.from({ length: loadingRows ?? 0 }).map((_, rowIndex) => (
                <LoadingRow
                  key={`loading-${rowIndex}`}
                  rowIndex={rowIndex}
                  columnsCount={columns.length}
                />
              ))}
          </tbody>
        </table>
      </div>
      {withPagination && totalItems > 0 && (
        <TablePagination
          pageIndex={pageIndex}
          nextDisabled={nextDisabled}
          prevDisabled={prevDisabled}
          totalPages={totalPages}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPrevPage={handlePrevPage}
          onNextPage={handleNextPage}
        />
      )}
    </>
  );
};

export const Table = memo(TableComponent) as typeof TableComponent;
